import narrationCleanupHelpers from "./echoNarrationCleanup";

const { sanitizeNarrationSourceText } = narrationCleanupHelpers;

const CLIENT_PDF_IMPORT_FALLBACK_BYTES = 4 * 1024 * 1024;
const MIN_MEANINGFUL_PDF_CHARS = 12;

export class EchoDocumentImportError extends Error {
  constructor(message, options = {}) {
    super(message);
    this.name = "EchoDocumentImportError";
    this.code = options.code || "document-import-failed";
    this.status = Number.isInteger(options.status) ? options.status : 0;
  }
}

function isPdfDocument(file) {
  const fileName = String(file?.name || "").toLowerCase();
  const mimeType = String(file?.type || "").toLowerCase();

  return fileName.endsWith(".pdf") || mimeType === "application/pdf";
}

function fileNameWithoutExtension(fileName) {
  return String(fileName || "")
    .trim()
    .replace(/\.[^/.]+$/, "");
}

function hasMeaningfulPdfText(text) {
  return sanitizeNarrationSourceText(text).replace(/\s+/g, "").length >= MIN_MEANINGFUL_PDF_CHARS;
}

async function loadBrowserPdfJs() {
  const browserScope = typeof window !== "undefined" ? window : {};

  if (!browserScope.pdfjsWorker?.WorkerMessageHandler) {
    try {
      const workerModule = await import("pdfjs-dist/legacy/build/pdf.worker.mjs");

      if (workerModule?.WorkerMessageHandler) {
        browserScope.pdfjsWorker = {
          ...(browserScope.pdfjsWorker || {}),
          WorkerMessageHandler: workerModule.WorkerMessageHandler
        };
      }
    } catch (error) {
      // Let pdf.js surface the runtime error if the worker bundle still cannot be loaded.
    }
  }

  return import("pdfjs-dist/legacy/build/pdf.mjs");
}

async function importPdfInBrowser(file) {
  const pdfjs = await loadBrowserPdfJs();
  const loadingTask = pdfjs.getDocument({
    data: new Uint8Array(await file.arrayBuffer()),
    disableFontFace: true,
    isEvalSupported: false,
    useWorkerFetch: false
  });

  try {
    const pdfDocument = await loadingTask.promise;
    const pages = [];

    try {
      for (let pageNumber = 1; pageNumber <= pdfDocument.numPages; pageNumber += 1) {
        const page = await pdfDocument.getPage(pageNumber);

        try {
          const textContent = await page.getTextContent();
          const pageText = sanitizeNarrationSourceText(
            Array.isArray(textContent?.items)
              ? textContent.items
                  .map((item) => {
                    if (typeof item?.str !== "string") {
                      return "";
                    }

                    return item.hasEOL ? `${item.str}\n` : item.str;
                  })
                  .join(" ")
              : ""
          );

          pages.push(pageText);
        } finally {
          if (typeof page.cleanup === "function") {
            page.cleanup();
          }
        }
      }
    } finally {
      if (typeof pdfDocument.destroy === "function") {
        await pdfDocument.destroy();
      }
    }

    const cleanedPages = pages.filter(Boolean);
    const combinedText = sanitizeNarrationSourceText(cleanedPages.join("\n\n"));
    const hasReadableText =
      cleanedPages.some((pageText) => hasMeaningfulPdfText(pageText)) ||
      hasMeaningfulPdfText(combinedText);

    if (!hasReadableText) {
      throw new EchoDocumentImportError(
        "This PDF appears to be scanned or image-only. PageTone currently supports text-based PDFs only.",
        {
          code: "unsupported-scanned-pdf",
          status: 422
        }
      );
    }

    return {
      title: fileNameWithoutExtension(file.name) || "Imported Document",
      fileType: "PDF",
      text: combinedText,
      meta: {
        pageCount: cleanedPages.length || pages.length
      }
    };
  } finally {
    if (typeof loadingTask.destroy === "function") {
      try {
        await loadingTask.destroy();
      } catch (error) {
        // Ignore cleanup failures from the PDF loader.
      }
    }
  }
}

export async function importEchoDocument(file) {
  const shouldUseBrowserPdfImport =
    isPdfDocument(file) &&
    Number(file?.size || 0) > CLIENT_PDF_IMPORT_FALLBACK_BYTES;

  if (shouldUseBrowserPdfImport) {
    return importPdfInBrowser(file);
  }

  const formData = new FormData();
  formData.append("file", file);

  const response = await fetch("/api/echo/import-document", {
    method: "POST",
    body: formData
  });

  const rawBody = await response.text();
  let parsedBody = null;

  try {
    parsedBody = rawBody ? JSON.parse(rawBody) : null;
  } catch (error) {
    parsedBody = null;
  }

  if (!response.ok) {
    const importError = new EchoDocumentImportError(
      parsedBody?.error || "Document import failed.",
      {
        code: parsedBody?.code || "document-import-failed",
        status: response.status
      }
    );

    if (
      isPdfDocument(file) &&
      (importError.code === "file-too-large" || importError.status === 413)
    ) {
      return importPdfInBrowser(file);
    }

    throw importError;
  }

  if (!parsedBody || typeof parsedBody.text !== "string") {
    throw new EchoDocumentImportError("Document import returned no readable text.", {
      code: "document-import-invalid-response",
      status: response.status
    });
  }

  return parsedBody;
}
